import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import { useSearchParams } from 'react-router-dom';
import { useInstanceId } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation.jsx';
import API_ENDPOINTS from '../config/api';

// specs/rag-source-heatmap.md — v3, Tab 2 (TIETOPOHJA).
//
// A fork of RagSourceMapV2Screen.jsx, not an edit of it — CLAUDE.md's
// redesign rule: the interaction model genuinely diverges (independent
// interactive zoom/pan per panel vs. v2's fixed auto-fit view, a
// scale-lock toggle, a teema filter facet, no Kuutio switcher at all —
// "one workspace is one corpus"). v1 and v2 stay exactly as shipped.
//
// IMPORTANT SCOPE NOTE (read before extending this file): the v3 spec's
// full architecture — corpus as a versioned/lockable object, a document
// ingestion tab, field-level metadata provenance — does not exist yet.
// This screen is a deliberate, flagged simplification: it still reads
// v2's existing instance-scoped backend endpoint (API_ENDPOINTS.RAG_SOURCE_MAP_V2),
// not a real corpus_id. It will need repointing once the corpus-versioning
// backend exists; nothing here should be read as "the URL scheme is settled."
//
// Layout (x/y/r for every circle) is still fully precomputed server-side and
// deterministic, per the spec's unchanged constraint. Only the VIEW
// transform (pan/zoom) is interactive and client-side — that's orthogonal to
// "same input → pixel-identical output," which is about the underlying data
// layout, not how a viewer chooses to look at it.

const MONO_FONT = "'JetBrains Mono', 'SF Mono', monospace";
const REFRESH_INTERVAL_MS = 20000;
const HIT_RADIUS = 22;
const PANEL_MARGIN = 44;
const SCALE_EXTENT = [0.4, 8];

function contextLabel(t, context) {
  const key = `ragSourceMap.contexts.${context}`;
  const translated = t(key);
  if (translated == null || translated === key) {
    return context.charAt(0).toUpperCase() + context.slice(1).replace(/_/g, ' ');
  }
  return translated;
}

// Interactive pan/zoom for one panel's <svg>, in real pixel space (not a
// viewBox auto-fit — d3-zoom's pointer math assumes the DOM element's actual
// rendered size, and mixing it with viewBox scaling makes pan/zoom feel
// wrong relative to the cursor). `onGesture` fires only for real user input
// (event.sourceEvent set — the same idiom v1's ragSourceMapLayout.js already
// uses to tell a gesture apart from a programmatic transform), which is what
// makes the scale-lock sync below safe from feedback loops: a synced,
// programmatic setScale() on the OTHER panel never re-fires this callback.
function usePanelZoom(svgRef, onGestureRef) {
  const [transform, setTransform] = useState(d3.zoomIdentity);
  const transformRef = useRef(d3.zoomIdentity);
  const zoomBehaviorRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return undefined;
    const svg = d3.select(svgRef.current);
    const zoomBehavior = d3.zoom()
      .scaleExtent(SCALE_EXTENT)
      .on('zoom', (event) => {
        transformRef.current = event.transform;
        setTransform(event.transform);
        if (event.sourceEvent) onGestureRef.current?.(event.transform);
      });
    svg.call(zoomBehavior);
    zoomBehaviorRef.current = zoomBehavior;
    return () => { svg.on('.zoom', null); zoomBehaviorRef.current = null; };
  }, [svgRef, onGestureRef]);

  const applyTransform = useCallback((next, animate = false) => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const sel = d3.select(svgRef.current);
    if (animate) sel.transition().duration(300).call(zoomBehaviorRef.current.transform, next);
    else sel.call(zoomBehaviorRef.current.transform, next);
  }, [svgRef]);

  const setScale = useCallback((k) => {
    const current = transformRef.current;
    applyTransform(new d3.ZoomTransform(k, current.x, current.y));
  }, [applyTransform]);

  // Fit the panel's precomputed content (radius r, centered at local 0,0) to
  // the container — the initial view, and re-applied whenever the data
  // itself changes (new corpus/panel), but never overwrites a view the
  // visitor has already interacted with in between.
  const fitTo = useCallback((width, height, contentRadius) => {
    if (!width || !height || !contentRadius) return;
    const k = Math.min(width, height) / (2 * (contentRadius + PANEL_MARGIN));
    applyTransform(new d3.ZoomTransform(k, width / 2, height / 2));
  }, [applyTransform]);

  const reset = useCallback((width, height, contentRadius) => fitTo(width, height, contentRadius), [fitTo]);

  return { transform, setScale, fitTo, reset };
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: radial-gradient(ellipse at 50% 40%, #14141f 0%, #0a0a12 60%, #050508 100%);
  color: white;
  position: relative;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  z-index: 10;
`;

const Title = styled.h1`
  color: rgba(255, 255, 255, 0.92);
  font-family: ${MONO_FONT};
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: -0.02em;
  margin: 0;
`;

const TopBarControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ToggleButton = styled.button`
  font-family: ${MONO_FONT};
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid ${props => (props.$active ? '#FACC15' : 'rgba(255,255,255,0.25)')};
  background: ${props => (props.$active ? 'rgba(250,204,21,0.15)' : 'transparent')};
  color: ${props => (props.$active ? '#FACC15' : 'rgba(255,255,255,0.7)')};

  &:hover { border-color: ${props => (props.$active ? '#FACC15' : 'rgba(255,255,255,0.5)')}; }
`;

const SplitCanvas = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const PanelColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  position: relative;

  & + & { border-left: 1px solid rgba(255, 255, 255, 0.08); }
`;

const PanelHeader = styled.div`
  padding: 14px 20px 8px;
  z-index: 5;
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PanelTitle = styled.div`
  font-family: ${MONO_FONT};
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
`;

const ScaleBadge = styled.div`
  font-family: ${MONO_FONT};
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  padding: 1px 6px;
`;

const PanelAxisLabel = styled.div`
  font-family: ${MONO_FONT};
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
  margin-top: 2px;
`;

const PanelCounter = styled.div`
  font-family: ${MONO_FONT};
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 4px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const FilterChip = styled.button`
  font-family: ${MONO_FONT};
  font-size: 0.66rem;
  letter-spacing: 0.03em;
  padding: 3px 9px;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid ${props => props.color}88;
  background: ${props => (props.$active ? `${props.color}33` : 'transparent')};
  color: ${props => (props.$active ? props.color : 'rgba(255,255,255,0.5)')};
`;

const SvgWrap = styled.div`
  flex: 1;
  min-height: 0;
  touch-action: none;
`;

const PanelSvg = styled.svg`
  display: block;
  cursor: grab;
  &:active { cursor: grabbing; }
`;

const ResetButton = styled.button`
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 6;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(20, 20, 28, 0.6);
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { color: #fff; border-color: rgba(255,255,255,0.4); }
`;

const Legend = styled.div`
  position: absolute;
  bottom: 16px;
  left: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: ${MONO_FONT};
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(10, 10, 16, 0.5);
  border-radius: 8px;
  padding: 8px 10px;
  z-index: 5;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendSwatch = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${props => props.color};
  display: inline-block;
`;

const StatusMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.55);
  font-family: ${MONO_FONT};
  font-size: 1rem;
  text-align: center;
`;

const DetailPanel = styled.div`
  position: absolute;
  top: 70px;
  right: 24px;
  width: 320px;
  max-width: calc(100% - 48px);
  max-height: calc(100% - 100px);
  overflow-y: auto;
  background: rgba(14, 14, 22, 0.88);
  border: 1px solid ${props => props.accent}55;
  border-radius: 10px;
  padding: 20px;
  color: white;
  font-family: ${MONO_FONT};
  backdrop-filter: blur(14px);
  z-index: 20;
  box-shadow: 0 0 30px ${props => props.accent}22;
`;

const DetailTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 8px;
  padding-right: 20px;
  line-height: 1.35;
`;

const DetailType = styled.div`
  font-size: 0.75rem;
  color: ${props => props.accent};
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailMeta = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 10px;
`;

const DetailDescription = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 16px;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  &:hover { color: white; }
`;

const NotRetrievedBadge = styled.div`
  display: inline-block;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.5);
  border: 1px dashed rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 1px 8px;
  margin: -6px 0 16px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailTotal = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 2px;
`;

const DetailTotalLabel = styled.div`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

const ContextRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 6px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

function Panel({
  panelId, panelData, title, labelNamespace, useDefaultView,
  teemaFilter, onToggleTeemaFilter,
  selected, hovered, onSelect, onHover, t,
  onGestureRef, apiRef,
}) {
  const layout = useDefaultView ? panelData.defaultLayout : panelData.usageLayout;
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const zoom = usePanelZoom(svgRef, onGestureRef);

  useEffect(() => { if (apiRef) apiRef.current = zoom; });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Re-fit whenever the underlying data extent changes (new corpus load,
  // toggling usage/default view — different radius) or on first real size —
  // but never on a re-render of the SAME data, so an in-progress zoom/pan
  // survives things like the 20s poll refresh.
  const fitKey = `${layout.radius}:${useDefaultView}:${size.width}:${size.height}`;
  const lastFitKeyRef = useRef(null);
  useEffect(() => {
    if (!size.width || !size.height) return;
    if (lastFitKeyRef.current === fitKey) return;
    lastFitKeyRef.current = fitKey;
    zoom.fitTo(size.width, size.height, layout.radius);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  const sizeLabel = useDefaultView ? t('ragSourceMapV2.legendSizeDefault') : t('ragSourceMapV2.legendSizeUsage');
  const teemaKeysPresent = [...new Set(layout.nodes.map(n => n.teema).filter(Boolean))];

  return (
    <PanelColumn>
      <PanelHeader>
        <PanelTitleRow>
          <PanelTitle>{title}</PanelTitle>
          <ScaleBadge>{Math.round(zoom.transform.k * 100)}%</ScaleBadge>
        </PanelTitleRow>
        <PanelAxisLabel>{t(`ragSourceMapV2.axis.${panelData.axisKey}`)}</PanelAxisLabel>
        <PanelCounter>
          {useDefaultView
            ? t('ragSourceMapV2.counterTotal', { total: panelData.counters.total })
            : t('ragSourceMapV2.counter', { total: panelData.counters.total, retrieved: panelData.counters.retrieved })}
        </PanelCounter>
        {onToggleTeemaFilter && teemaKeysPresent.length > 0 && (
          <FilterRow>
            {teemaKeysPresent.map(teema => (
              <FilterChip
                key={teema}
                color="#94A3B8"
                $active={teemaFilter.has(teema)}
                onClick={() => onToggleTeemaFilter(teema)}
              >
                {t(`ragSourceMapV2.teema.${teema}`)}
              </FilterChip>
            ))}
          </FilterRow>
        )}
      </PanelHeader>

      <SvgWrap ref={wrapRef}>
        <PanelSvg ref={svgRef} width={size.width} height={size.height}>
          <g transform={zoom.transform.toString()}>
            {layout.clusters.map(cluster => {
              const color = panelData.clusterColors[cluster.key];
              return (
                <g key={cluster.key}>
                  <circle
                    cx={cluster.center.x} cy={cluster.center.y} r={cluster.radius}
                    fill="none" stroke={color} strokeOpacity={cluster.empty ? 0.3 : 0.45}
                    strokeWidth={1.5 / zoom.transform.k} strokeDasharray={cluster.empty ? '3,4' : '5,4'}
                  />
                  <text
                    x={cluster.labelAnchor.x} y={cluster.labelAnchor.y}
                    fill={color} fillOpacity={cluster.empty ? 0.55 : 1}
                    textAnchor="middle" fontFamily={MONO_FONT}
                    fontSize={12 / zoom.transform.k} fontWeight={600} letterSpacing="0.06em"
                  >
                    {t(`ragSourceMapV2.${labelNamespace}.${cluster.key}`).toUpperCase()}
                  </text>
                </g>
              );
            })}

            {layout.nodes.map(node => {
              const color = panelData.clusterColors[node.cluster];
              const filled = useDefaultView || node.retrieved;
              const isHovered = hovered?.panelId === panelId && hovered.node.id === node.id;
              const isSelected = selected?.panelId === panelId && selected.node.id === node.id;
              const isFilteredOut = teemaFilter && teemaFilter.size > 0 && node.teema && !teemaFilter.has(node.teema);
              const hitR = Math.max(node.r, HIT_RADIUS / zoom.transform.k);
              return (
                <g key={node.id} opacity={isFilteredOut ? 0.08 : 1}>
                  <circle
                    cx={node.x} cy={node.y} r={hitR} fill="transparent"
                    style={{ cursor: isFilteredOut ? 'default' : 'pointer', pointerEvents: isFilteredOut ? 'none' : 'auto' }}
                    onClick={() => onSelect({ panelId, node })}
                    onMouseEnter={() => onHover({ panelId, node })}
                    onMouseLeave={() => onHover(null)}
                  >
                    <title>{node.title}</title>
                  </circle>
                  <circle
                    cx={node.x} cy={node.y} r={node.r}
                    fill={filled ? color : 'none'} fillOpacity={filled ? 0.85 : 0}
                    stroke={color} strokeOpacity={isHovered ? 0.95 : (filled ? 0.7 : 0.6)}
                    strokeWidth={(filled ? 1 : 1.5) / zoom.transform.k}
                    style={{ pointerEvents: 'none' }}
                  />
                  {isSelected && (
                    <circle
                      cx={node.x} cy={node.y} r={node.r + 4 / zoom.transform.k}
                      fill="none" stroke={color} strokeWidth={1 / zoom.transform.k} strokeOpacity={0.9}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                </g>
              );
            })}
          </g>
        </PanelSvg>
        <ResetButton
          title={t('ragSourceMap.resetView')}
          onClick={() => zoom.reset(size.width, size.height, layout.radius)}
        >
          {'⟲'}
        </ResetButton>
      </SvgWrap>

      <Legend>
        {panelData.clusterOrder.map(key => (
          <LegendRow key={key}>
            <LegendSwatch color={panelData.clusterColors[key]} />
            {t(`ragSourceMapV2.${labelNamespace}.${key}`)}
          </LegendRow>
        ))}
        <LegendRow style={{ marginTop: 4, opacity: 0.7 }}>
          <LegendSwatch color="rgba(255,255,255,0.3)" style={{ width: 4, height: 4 }} />
          {sizeLabel}
        </LegendRow>
      </Legend>
    </PanelColumn>
  );
}

function CorpusTietopohjaScreen() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const instanceId = useInstanceId(searchParams);

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [useDefaultView, setUseDefaultView] = useState(false);
  const [scaleLocked, setScaleLocked] = useState(false);
  const [teemaFilter, setTeemaFilter] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  const leftGestureRef = useRef(null);
  const rightGestureRef = useRef(null);
  const leftZoomApiRef = useRef(null);
  const rightZoomApiRef = useRef(null);

  useEffect(() => {
    leftGestureRef.current = (transform) => {
      if (!scaleLocked) return;
      rightZoomApiRef.current?.setScale(transform.k);
    };
    rightGestureRef.current = (transform) => {
      if (!scaleLocked) return;
      leftZoomApiRef.current?.setScale(transform.k);
    };
  }, [scaleLocked]);

  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.RAG_SOURCE_MAP_V2(instanceId));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        if (cancelled) return;
        setData(json);
        setStatus((json.perustietopohja.counters.total + json.kuutio.counters.total) > 0 ? 'ready' : 'empty');
        setSelected(prev => {
          if (!prev) return prev;
          const panel = prev.panelId === 'kuutio' ? json.kuutio : json.perustietopohja;
          const stillExists = panel.usageLayout.nodes.some(n => n.id === prev.node.id);
          return stillExists ? prev : null;
        });
      } catch (err) {
        if (!cancelled) {
          console.error('CorpusTietopohjaScreen: failed to load source map', err);
          setStatus('error');
        }
      }
    };

    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [instanceId]);

  const toggleTeemaFilter = (teema) => {
    setTeemaFilter(prev => {
      const next = new Set(prev);
      if (next.has(teema)) next.delete(teema);
      else next.add(teema);
      return next;
    });
  };

  return (
    <Container>
      <TopBar>
        <Title>{t('ragSourceMapV2.title')}</Title>
        <TopBarControls>
          <ToggleButton $active={scaleLocked} onClick={() => setScaleLocked(v => !v)}>
            {t('ragSourceMapV2.lockScale')}
          </ToggleButton>
          <ToggleButton $active={useDefaultView} onClick={() => setUseDefaultView(v => !v)}>
            {t('ragSourceMapV2.defaultViewToggle')}
          </ToggleButton>
        </TopBarControls>
      </TopBar>

      {status === 'loading' && <StatusMessage>{t('ragSourceMapV2.loading')}</StatusMessage>}
      {status === 'empty' && <StatusMessage>{t('ragSourceMapV2.empty')}</StatusMessage>}
      {status === 'error' && <StatusMessage>{t('ragSourceMapV2.error')}</StatusMessage>}

      {data && status === 'ready' && (
        <SplitCanvas>
          <Panel
            panelId="perustietopohja"
            panelData={data.perustietopohja}
            title={t('ragSourceMapV2.perustietopohja')}
            labelNamespace="teema"
            useDefaultView={useDefaultView}
            selected={selected} hovered={hovered}
            onSelect={setSelected} onHover={setHovered}
            t={t}
            onGestureRef={leftGestureRef}
            apiRef={leftZoomApiRef}
          />
          <Panel
            panelId="kuutio"
            panelData={data.kuutio}
            title={data.kuutio.datasetName || t('ragSourceMapV2.title')}
            labelNamespace="voices"
            useDefaultView={useDefaultView}
            teemaFilter={teemaFilter}
            onToggleTeemaFilter={toggleTeemaFilter}
            selected={selected} hovered={hovered}
            onSelect={setSelected} onHover={setHovered}
            t={t}
            onGestureRef={rightGestureRef}
            apiRef={rightZoomApiRef}
          />
        </SplitCanvas>
      )}

      {selected && (() => {
        const panel = selected.panelId === 'kuutio' ? data.kuutio : data.perustietopohja;
        const labelNamespace = selected.panelId === 'kuutio' ? 'voices' : 'teema';
        const node = selected.node;
        const accent = panel.clusterColors[node.cluster];
        const meta = [node.publisher, node.publicationYear].filter(Boolean).join(' · ');
        return (
          <DetailPanel accent={accent}>
            <CloseButton onClick={() => setSelected(null)}>&times;</CloseButton>
            <DetailTitle>{node.title}</DetailTitle>
            <DetailType accent={accent}>{t(`ragSourceMapV2.${labelNamespace}.${node.cluster}`)}</DetailType>
            {!node.retrieved && !useDefaultView && <NotRetrievedBadge>{t('ragSourceMapV2.notRetrieved')}</NotRetrievedBadge>}

            {meta && <DetailMeta>{meta}</DetailMeta>}
            {node.description && <DetailDescription>{node.description}</DetailDescription>}

            {useDefaultView ? (
              <>
                <DetailTotal>{node.chunkCount}</DetailTotal>
                <DetailTotalLabel>{t('ragSourceMapV2.chunksLabel')}</DetailTotalLabel>
              </>
            ) : (
              <>
                <DetailTotal>{node.totalRetrievals}</DetailTotal>
                <DetailTotalLabel>{t('ragSourceMapV2.retrievals')}</DetailTotalLabel>
                <div>
                  {Object.entries(node.byContext || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([context, count]) => (
                      <ContextRow key={context}>
                        <span>{contextLabel(t, context)}</span>
                        <span>{count}</span>
                      </ContextRow>
                    ))}
                </div>
              </>
            )}
          </DetailPanel>
        );
      })()}
    </Container>
  );
}

export default CorpusTietopohjaScreen;
